import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
// import { useNavigate } from "react-router-dom";
import { Person } from "@mui/icons-material";

// Components
import SearchComponent from "../../components/Search";
import PaginationComponent from "../../components/Pagination/Pagination-O";
import { GroupAdd } from "@mui/icons-material";
import { debounce } from "lodash";

// Redux actions
import { getAllStoreManagers } from "../../redux/StoreManagement/storeManagementActions";

const StoreManagersTable = () => {
  const dispatch = useDispatch();
  // const navigate = useNavigate();

  // Redux state
  const {
    storeManagersData,
    storeManagersLoading,
    storeManagersError,
    storeManagersTotalCount,
    storeManagersCurrentPage,
    storeManagersTotalPages,
  } = useSelector((state) => state.storeManagement);

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [status, setStatus] = useState("");

  // Get counts for tabs
  const getTabCounts = useMemo(() => {
    if (!storeManagersData || !Array.isArray(storeManagersData)) {
      return { all: 0, pendingRequest: 0 };
    }
    const pendingRequest = storeManagersData.filter(
      (manager) => manager.invite_status === "PENDING"
    ).length;

    return {
      all: storeManagersTotalCount || 0,
      pendingRequest,
    };
  }, [storeManagersData, storeManagersTotalCount]);

  // Tab options - removed Pending Deliveries
  const tabs = [
    { id: "All", label: "All", count: getTabCounts.all },
    {
      id: "Pending Request",
      label: "Pending Request",
      count: getTabCounts.pendingRequest,
    },
  ];

 

  // Handle search
const debouncedSearch = useMemo(() => {
  return debounce((value) => {
    dispatch(
      getAllStoreManagers({
        page: 1,
        page_size: pageSize,
        search: value,
      })
    );
  }, 500); // 500ms debounce
}, [dispatch, pageSize]);

// Handle search input change
const handleSearch = (e) => {
  const value = e.target.value;
  setSearchValue(value);
  setCurrentPage(1);
  debouncedSearch(value);
};


useEffect(() => {
  return () => {
    debouncedSearch.cancel(); // Cancel debounce on unmount
  };
}, [debouncedSearch]);


  // Handle pagination
  const handlePageChange = (paginationData) => {
    let newPage = currentPage;

    if (typeof paginationData === "object") {
      if (paginationData.page !== undefined) {
        newPage = paginationData.page;
      }
    } else {
      newPage = paginationData;
    }

    setCurrentPage(newPage);

    // Fetch data for new page
    dispatch(
      getAllStoreManagers({
        page: newPage,
        page_size: pageSize,
        search: searchValue,
      })
    );
  };

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setCurrentPage(1);
    setSearchValue("");

    let newStatus = "";

    if (tabId === "All") {
      newStatus = "";
    } else if (tabId === "Pending Request") {
      newStatus = "pending";
    }

    setStatus(newStatus);

    dispatch(
      getAllStoreManagers({
        page: 1,
        page_size: pageSize,
        search: "",
        invite_status: newStatus, // ← Use the new value directly
      })
    );
  };

  // Handle actions
  // const handleViewManager = (managerId) => {
  //   navigate(`/store-management/managers/${managerId}`);
  // };

  // Initial fetch
  useEffect(() => {
    dispatch(
      getAllStoreManagers({
        page: 1,
        page_size: pageSize,
        search: "",
        invite_status: status,
      })
    );
  }, [dispatch, pageSize, status]);

  return (
    <div className="bg-white rounded-lg border border-[#E1E6E1]">
      {/* Header */}
      <div className="p-4 border-b border-[#E1E6E1]">
        <div className="flex flex-col gap-4 mb-4">
          <h3 className="text-lg font-semibold text-[#161D17]">
            Store Manager
          </h3>

          {/* Search and Export on same line */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <SearchComponent
                searchValue={searchValue}
                handleChange={handleSearch}
                placeholder="Search store managers..."
              />
            </div>
            <button className="flex items-center gap-2 border border-[#2B8C34] text-[#2B8C34] px-6 py-2.5 rounded-lg hover:bg-[#2B8C34] hover:text-white transition-colors whitespace-nowrap font-medium min-h-[50px]">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span className="text-sm font-medium">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs - Using Inventory Design Pattern */}
      <div className="px-4 pt-4">
        <div className="flex items-center border max-w-2xl border-[#E1E6E1] bg-[#F6F7F6] rounded-lg overflow-hidden">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-3 py-2 text-center flex-1 text-sm font-semibold flex items-center justify-center gap-2 ${
                activeTab === tab.id ? "bg-white rounded-lg" : "bg-[#F6F7F6]"
              }`}
            >
              <span>{tab.label}</span>
              <span
                className={`px-2 py-1 rounded-[4px] text-xs font-medium ${
                  activeTab === tab.id
                    ? "bg-[#DBFFDF] text-[#2B8C34]"
                    : "bg-[#E1E6E1] text-[#5C715E]"
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Table Content */}
      <div className="overflow-x-auto relative py-4">
        {storeManagersLoading ? (
          <div className="animate-pulse w-full px-4 mb-4">
            {/* Table header shimmer */}
            <div className="h-12 bg-gray-200 rounded-lg w-full mb-2"></div>
            {/* Table rows shimmer */}
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full mb-2"></div>
            <div className="h-10 bg-gray-200 rounded-lg w-full"></div>
          </div>
        ) : storeManagersError ? (
          <div className="p-8 text-center">
            <div className="text-red-600 mb-2">
              Error loading store managers
            </div>
            <div className="text-sm text-gray-500 mb-4">
              {storeManagersError}
            </div>
            <button
              onClick={() =>
                dispatch(getAllStoreManagers({ page: 1, page_size: pageSize }))
              }
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Try Again
            </button>
          </div>
        ) : !storeManagersData || storeManagersData.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4">
            <div className="w-[48px] flex justify-center items-center h-[48px] rounded-full border border-[#E1E6E1] bg-[#F6F7F6] mb-4">
              <GroupAdd className="text-[#5F6D60] w-6 h-6" />
            </div>
            <p className="text-sm font-semibold text-[#5F6D60] text-center">
              {searchValue
                ? "No store managers match your search criteria."
                : "No store managers have been added yet."}
            </p>
          </div>
        ) : (
          <>
            {/* Table - Using Inventory Design Pattern */}
            <table
              className="w-full text-left rounded-lg overflow-hidden border border-[#E1E6E1] mx-4"
              style={{ width: "calc(100% - 2rem)" }}
            >
              <thead className="bg-[#F6F7F6] border-[#E1E6E1]">
                <tr className="text-sm">
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tl-lg rounded-bl-lg">
                    Name
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Phone Number
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Assigned Store
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    State
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60]">
                    Date Assigned
                  </th>
                  <th className="px-4 py-4 font-semibold text-[#5F6D60] rounded-tr-lg rounded-br-lg">
                    Last Active
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white border-[#E1E6E1]">
                {storeManagersData && storeManagersData.length > 0
                  ? storeManagersData.map((manager) => (
                      <tr
                        key={manager.id}
                        className="text-sm cursor-pointer hover:bg-[#EDF7EE] border-b border-[#E1E6E1]"

                        // onClick={() => handleViewManager(manager.id)}
                      >
                        <td className="px-4 py-4 font-normal text-[#5F6D60] whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <Person className="h-4 w-4 text-primary" />
                              </div>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-semibold text-[#161D17]">
                                {manager.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                          {manager.phone}
                        </td>
                        <td className="px-4 py-4 text-[#5F6D60] font-semibold">
                          {manager.assigned_store}
                        </td>
                        <td className="px-4 py-4 text-[#5F6D60]">
                          <p className="border w-fit py-1 px-2 border-[#E1E6E1] rounded-md font-medium">
                            {manager.state}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-[#344335]">
                          {manager.date_assigned}
                        </td>
                        <td className="px-4 py-4 text-[#344335]">
                          {manager.last_active}
                        </td>
                      </tr>
                    ))
                  : null}
              </tbody>
            </table>

            {/* Pagination - Updated to use server pagination */}
            <div className="px-4 py-4 border-t border-[#E1E6E1]">
              <PaginationComponent
                page={storeManagersCurrentPage || currentPage}
                totalItems={storeManagersTotalCount || 0}
                totalPages={storeManagersTotalPages}
                onPageChange={handlePageChange}
                pageSize={pageSize}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreManagersTable;
